import PropTypes from "prop-types";
import { Link } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

import logoStopRasismWhite from "assets/images/stop-rasism-2-white.png";
import siglaGuvernului from "assets/images/sigla-guvernului.png";

const FOOTER_BG = "#061D36";
const HEADING_COLOR = "#ffffff";
const LINK_COLOR = "rgba(255,255,255,0.72)";
const LINK_HOVER_COLOR = "#FFC107";
const MUTED_COLOR = "rgba(255,255,255,0.6)";
const DIVIDER_COLOR = "rgba(255,255,255,0.1)";

const linkSx = {
  display: "block",
  color: LINK_COLOR,
  fontSize: "0.88rem",
  lineHeight: 1.45,
  mb: 1,
  textDecoration: "none",
  transition: "color 0.15s ease",
  "&:hover": { color: LINK_HOVER_COLOR },
  "&:focus": { color: LINK_HOVER_COLOR, outline: "none" },
};

function FooterLink({ to, children }) {
  return (
    <SoftBox component={Link} to={to} sx={linkSx}>
      {children}
    </SoftBox>
  );
}
FooterLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function FooterLinkDead({ children }) {
  return (
    <SoftBox
      component="a"
      href="#"
      onClick={(e) => e.preventDefault()}
      sx={{ ...linkSx, cursor: "pointer" }}
    >
      {children}
    </SoftBox>
  );
}
FooterLinkDead.propTypes = {
  children: PropTypes.node.isRequired,
};

function ColumnHeading({ children }) {
  return (
    <SoftTypography
      sx={{
        color: HEADING_COLOR,
        fontWeight: 700,
        fontSize: "0.98rem",
        mb: 2,
        letterSpacing: "0.01em",
      }}
    >
      {children}
    </SoftTypography>
  );
}
ColumnHeading.propTypes = {
  children: PropTypes.node.isRequired,
};

function Disclaimer({ icon, children }) {
  return (
    <SoftBox sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
      <Icon
        sx={{
          color: MUTED_COLOR,
          fontSize: "1rem !important",
          mt: "2px",
          flexShrink: 0,
        }}
      >
        {icon}
      </Icon>
      <SoftTypography
        sx={{
          color: "rgba(255,255,255,0.78)",
          fontSize: "0.82rem",
          lineHeight: 1.6,
          "& strong": { color: "#ffffff", fontWeight: 700 },
        }}
      >
        {children}
      </SoftTypography>
    </SoftBox>
  );
}
Disclaimer.propTypes = {
  icon: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function Footer() {
  const year = new Date().getFullYear();

  return (
    <SoftBox
      component="footer"
      sx={{
        bgcolor: FOOTER_BG,
        color: "#fff",
        pt: { xs: 5, md: 7 },
        pb: { xs: 3, md: 4 },
      }}
    >
      <SoftBox sx={{ maxWidth: "1320px", mx: "auto", px: { xs: 3, md: 6, lg: 10 } }}>
        {/* ── Coloane principale ── */}
        <Grid container spacing={{ xs: 4, md: 4 }}>
          {/* Brand */}
          <Grid item xs={12} md={4}>
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Box
                component="img"
                src={siglaGuvernului}
                alt="Guvernul României"
                sx={{ width: 56, height: 56, objectFit: "contain", display: "block", flexShrink: 0 }}
              />
              <Box
                aria-hidden="true"
                sx={{ width: "1.5px", height: "42px", bgcolor: "rgba(255,255,255,0.35)", flexShrink: 0 }}
              />
              <Box
                component="img"
                src={logoStopRasismWhite}
                alt="Stop Rasism"
                sx={{ height: "44px", display: "block" }}
              />
            </SoftBox>
            <SoftTypography
              sx={{
                color: MUTED_COLOR,
                fontSize: "0.88rem",
                lineHeight: 1.6,
                maxWidth: "320px",
              }}
            >
              Platformă oficială pentru raportarea incidentelor antisemite, rasiste și xenofobe.
            </SoftTypography>
          </Grid>

          {/* Platformă */}
          <Grid item xs={6} sm={4} md={2}>
            <ColumnHeading>Platformă</ColumnHeading>
            <FooterLink to="/status-sesizare">Verifică statusul</FooterLink>
            <FooterLink to="/formular-sesizare">Depune o sesizare</FooterLink>
          </Grid>

          {/* Resurse (fără „Întrebări frecvente”) */}
          <Grid item xs={6} sm={4} md={3}>
            <ColumnHeading>Resurse</ColumnHeading>
            <FooterLink to="/ce-trebuie-sa-stii">Bibliotecă de informații</FooterLink>
            <FooterLink to="/drepturile-victimelor">Drepturile victimelor</FooterLink>
            <FooterLink to="/ghid">Ghid de utilizare</FooterLink>
          </Grid>

          {/* Legal — link-uri dead pentru moment */}
          <Grid item xs={12} sm={4} md={3}>
            <ColumnHeading>Legal</ColumnHeading>
            <FooterLinkDead>Termeni și condiții</FooterLinkDead>
            <FooterLinkDead>Politica de confidențialitate</FooterLinkDead>
            <FooterLinkDead>Politica de cookies</FooterLinkDead>
          </Grid>
        </Grid>

        {/* ── Separator ── */}
        <SoftBox
          sx={{
            height: "1px",
            bgcolor: DIVIDER_COLOR,
            my: { xs: 3.5, md: 4.5 },
          }}
        />

        {/* ── Disclaimere ── */}
        <SoftBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, md: 1.75 },
            mb: { xs: 3.5, md: 4 },
          }}
        >
          <Disclaimer icon="campaign">
            Platforma StopRasism nu se substituie procedurilor legale în vigoare. Răspunsurile la
            petițiile depuse prin această platformă vor fi comunicate, <strong>în primul rând</strong>,
            direct de către <strong>instituția competentă</strong> care a soluționat petiția. Ele vor
            fi transmise și prin intermediul platformei.
          </Disclaimer>
          <Disclaimer icon="info">
            Urgențele <strong>nu</strong> pot fi semnalate prin intermediul acestui site. Dacă situația
            dumneavoastră reprezintă o urgență, <strong>vă rugăm apelați 112</strong>.
          </Disclaimer>
        </SoftBox>

        {/* ── Separator ── */}
        <SoftBox sx={{ height: "1px", bgcolor: DIVIDER_COLOR, mb: { xs: 2.5, md: 3 } }} />

        {/* ── Copyright row ── */}
        <SoftBox
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", md: "center" },
            gap: { xs: 1, md: 2 },
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <SoftTypography
            sx={{
              color: "rgba(255,255,255,0.55)",
              fontSize: "0.78rem",
              lineHeight: 1.5,
            }}
          >
            © {year} StopRasism.ro — Toate drepturile rezervate.
          </SoftTypography>
          <SoftTypography
            sx={{
              color: "rgba(255,255,255,0.55)",
              fontSize: "0.78rem",
              lineHeight: 1.5,
              textAlign: { xs: "center", md: "right" },
            }}
          >
            Platforma a fost dezvoltată și este întreținută de{" "}
            <Box
              component="a"
              href="https://ici.ro"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "rgba(255,255,255,0.78)",
                fontWeight: 600,
                textDecoration: "none",
                transition: "color 0.15s ease",
                "&:hover": { color: LINK_HOVER_COLOR },
                "&:focus": { color: LINK_HOVER_COLOR, outline: "none" },
              }}
            >
              ICI București
            </Box>
            .
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

export default Footer;
